
import { Component, Prop } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import { hslColorFromName } from "@/utils/hslColorFromName";

import GetRelativeRouteNameMixin from "@/mixins/route/GetRelativeRouteNameMixin.vue";
import TableListChipNew from "@/components/table/table-list-chip-new.vue";
import CardBodyItem from "@/components/card/CardBodyItem.vue";
import Card from "@/components/card/Card.vue";
import ProjectModal from "@/components/companyProjects/ProjectModal.vue";
import CardInfoChip from "@/components/card/CardInfoChip.vue";
import CollapsibleDescription from "@/components/common/CollapsibleDescription.vue";
import CircleWithInitials from "@/components/card/CircleWithInitials.vue";
import UiButton from "@/components/button/ui-button.vue";
import ActivityFormModal from "@/components/projects/activities/ActivityFormModalNew.vue";
import ColorIconContainer from "@/components/icons/ColorIconContainer.vue";
import ChatIcon from "@/components/icons/ChatIcon.vue";
import ViewIcon from "@/components/icons/ViewIcon.vue";
import ViewIconBlack from "@/components/icons/ViewIconBlack.vue";
import CreateGroupConversationModal from "@/components/inbox/CreateGroupConversationModal.vue";
import ConversationUserDataForModal from "@/entities/ConversationUserDataForModal";
import ActivityHoursUnconfirmedAlert from "@/components/projects/activities/ActivityHoursUnconfirmedAlert.vue";
import ProjectActivitySmallCard from "@/components/companyProjects/ProjectActivitySmallCard.vue";
import RouteNames from "@/entities/RouteNames";
import Roles from "@/entities/Roles";
import moment from "moment";
import getFullNameOrPlaceholder from "@/helpers/getFullNameOrPlaceholder";
import LocationIcon from "@/components/icons/LocationIconBlack.vue";
import { Project, Project_Activity, User } from "@/generated/graphql";
import ItemChipsWithMore from "@/components/common/ItemChipsWithMore.vue";
import ItemChipsData from "@/entities/ItemChipsData";
import RouteParams from "@/entities/RouteParams";
import ActionSection from "../card/ActionSection.vue";
import MessageIcon from "../icons/MessageIcon.vue";
import BadgeColor from "@/entities/BadgeColor";
import Badge from "../common/BadgeNew.vue";

interface IActionDefault {
  leftTitle: string;
  leftValue: number | string;
  rightTitle: string;
  rightValue: number | string;
}

interface IActionError {
  title: string;
  description: number | string;
  actionButton: string;
}

interface IActionSection {
  state: boolean;
  default: IActionDefault;
  // error: IActionError;
}

interface ProjectNew extends Project {
  project_status: string;
}

@Component({
  components: {
    TableListChipNew,
    CardBodyItem,
    ViewIconBlack,
    LocationIcon,
    Card,
    ProjectModal,
    CardInfoChip,
    CircleWithInitials,
    UiButton,
    CollapsibleDescription,
    ActivityFormModal,
    ColorIconContainer,
    ChatIcon,
    ViewIcon,
    MessageIcon,
    CreateGroupConversationModal,
    ActivityHoursUnconfirmedAlert,
    ItemChipsWithMore,
    ProjectActivitySmallCard,
    ActionSection,
    Badge,
  },
})
export default class ProjectCard extends mixins(GetRelativeRouteNameMixin) {
  @Prop({ type: Object, required: true }) readonly project!: ProjectNew;
  @Prop({ type: String, default: undefined }) readonly companyId!: string;
  @Prop({ type: String, default: RouteNames.PROJECT_ACTIVITY })
  readonly projectDetailsRoute!: RouteNames;
  @Prop({ type: Boolean, default: false }) readonly canEdit!: boolean;
  @Prop({ type: Boolean, default: false }) readonly hideDescription!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly alertUncofirmedActivities!: boolean;
  @Prop({ type: Boolean, default: false }) readonly value!: boolean;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Boolean, default: false })
  readonly isWithActivities!: boolean;

  BadgeColor: typeof BadgeColor = BadgeColor;
  showActivities = false;
  dialog: boolean = false;
  activityModalOpened = false;
  newConversationDialog: boolean = false;

  projectStatus = {
    UPCOMING: "UPCOMING",
    NEW_VISITS: "NEW_VISITS",
    NO_UPCOMING: "NO_UPCOMING",
  };

  get getProjectStatus(): string {
    const activities = this.project.project_activities || [];
    const now = new Date();
    // If all activities are completed
    if (activities.length > 0 && activities.every((a) => a.completed)) {
      return this.projectStatus.NO_UPCOMING;
    }

    // If any future activity is not accepted and not completed → New Visit
    const hasNewVisit = activities.some(
      (a) =>
        new Date(a.start_date) > now &&
        (!a.accepted || a.accepted === null) &&
        !a.completed
    );
    if (hasNewVisit) {
      return this.projectStatus.NEW_VISITS;
    }

    // If any future activity is not completed → Upcoming
    const hasUpcoming = activities.some(
      (a) => new Date(a.start_date) > now && !a.completed
    );
    if (hasUpcoming) {
      return this.projectStatus.UPCOMING;
    }

    // Else → No Upcoming Visits
    return this.projectStatus.NO_UPCOMING;
  }


  get initial(): string {
    const firstName = this.project.manager.user?.given_name;
    const lastName = this.project.manager.user?.family_name;

    if (!firstName && !lastName) {
      return "";
    }

    const givenName = firstName ? firstName.trimLeft()[0] : "";
    const familyName = lastName ? lastName.trimLeft()[0] : "";
    return `${givenName}${familyName}`;
  }

  get bgStyle(): object {
    return {
      background: hslColorFromName(this.initial || ""),
    };
  }

  get showActivitiesBtnTitle(): string {
    return this.showActivities ? "Hide Visits" : "Show Visits";
  }

  get projectDetails(): RouteParams<{ id: string }> {
    return {
      name: RouteNames.PROJECT_DETAILS,
      params: {
        id: this.project.id,
      },
    };
  }

  get projectManager(): string {
    const givenName = this.project?.manager?.user.given_name;
    const familyName = this.project?.manager?.user.family_name;
    return getFullNameOrPlaceholder(givenName, familyName);
  }

  get hasUnconfirmedHours(): boolean {
    if (!this.alertUncofirmedActivities || !this.project?.project_activities) {
      return false;
    }

    return this.project.project_activities.some(
      ({ requested_hours, confirmed_hours }) =>
        Boolean(requested_hours) && !confirmed_hours
    );
  }

  get title(): string {
    return this.project.client_name || " - ";
  }

  get canEditProject(): boolean {
    return this.canEdit && !!this.companyId && !this.project.completed;
  }

  // get location(): string {
  // 	return getLocationInfo(this.project);
  // }

  get createdDate(): string {
    if (this.project.created_at) {
      return this.formattedDate(this.project.created_at);
    }
    return "n/a";
  }

  get startDate(): string {
    return this.formattedDate(this.project.start_date);
  }

  get endDate(): string {
    return this.formattedDate(this.project.end_date);
  }

  get activities(): Project_Activity[] {
    return this.project.project_activities;
  }

  get hasParticipants(): boolean {
    return this.projectUsers.length > 0;
  }

  get projectUsers(): ConversationUserDataForModal[] {
    const { project_activities: activities } = this.project;
    const manager = this.project.manager?.user;

    if (!activities.length && !manager) {
      return [];
    }

    const uniqueIds = new Set();

    const uniqueUsers = activities.reduce(
      (arr: ConversationUserDataForModal[], { contractor }) => {
        const user = contractor?.user;
        if (!user) {
          return arr;
        }
        const currentStepContractors: ConversationUserDataForModal[] = [];

        if (!uniqueIds.has(user.id)) {
          currentStepContractors.push({
            userId: user.id,
            avatar: user.avatar,
            name: `${user.given_name} ${user.family_name}`,
          });
          uniqueIds.add(user.id);
        }

        return arr.concat(currentStepContractors);
      },
      []
    );

    if (!manager || this.currentUser.role === Roles.MANAGER) {
      return uniqueUsers;
    }

    return [
      ...uniqueUsers,
      {
        userId: manager.id,
        avatar: manager.avatar,
        name: `${manager.given_name} ${manager.family_name} (manager)`,
      },
    ];
  }
  get totalActivities(): number | undefined {
    const getActiveTasks = this.project.project_activities.filter(
      (item) => item.accepted === null && !item.completed
    );
    const activities = getActiveTasks.length;
    return activities;
  }


  get getActionSectionValues(): IActionSection {
    const offerdVisits = this.totalActivities;
    let assignedVisits = 0;
    this.project.project_activities.forEach((projectActivity) => {
      if (projectActivity.accepted) {
        assignedVisits++;
      }
    });


    const actionSectionResult: IActionSection = {
      state: false,
      default: {
        leftTitle: "Offered Visits",
        leftValue: offerdVisits,
        rightTitle: "Assigned visits",
        rightValue: assignedVisits,
      },
    };
    return actionSectionResult;
  }

  formattedDate(date: string): string {
    if (date) {
      return moment.utc(date).format("DD MMM, YYYY");
    } else {
      return "";
    }
  }
  // return new Date(date).toLocaleDateString("en-ca", {
  //   year: "numeric",
  //   month: "short",
  //   day: "numeric",
  // });

  handleShowActivities() {
    this.showActivities = !this.showActivities;
  }
  handleEditClick(): void {
    this.dialog = true;
  }

  openActivityModal(): void {
    this.activityModalOpened = true;
  }

  onActivityListItemAdded(): void {
    this.$router.push({
      name: this.projectDetailsRoute,
      params: { id: this.project.id },
    });
  }

  groupMessage(): void {
    this.newConversationDialog = true;
  }

  message(): void {
    this.$router.push({
      name: RouteNames.INBOX,
      query: {
        recipientUserId: this.project.manager.user.id,
      },
    });
  }
}
